"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight } from "@/components/icons";
import Link from "next/link";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { buttonVariants, EyebrowBadge, HeroGlow, PricingCtaSection } from "@/components/ui/";
import { STATS, LINKEDIN_JOBS_URL } from "@/lib/constants";
import { useUptime } from "@/hooks/use-uptime";
import { whyItems, hiringSteps, benefits } from "./data";

const MotionDiv = motion.div;

export default function CareersSection() {
  const prefersReducedMotion = useReducedMotion();
  const uptime = useUptime();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    <main id="main-content" tabIndex={-1}>
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-24"
        aria-labelledby="careers-heading"
      >
        <HeroGlow align="left" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)}>
            <EyebrowBadge>Now hiring</EyebrowBadge>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.08)}>
            <h1
              id="careers-heading"
              className="text-foreground mb-8 max-w-4xl text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Work at Proxidize.
            </h1>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.16)} className="max-w-2xl">
            <p className="text-muted-foreground light:text-foreground/80 text-base leading-relaxed sm:text-lg">
              Proxidize is a managed cloud proxy provider serving {STATS.customers} teams in{" "}
              {`${STATS.countries} countries`}. We&apos;re a small team solving hard networking
              problems, and we&apos;re growing.
            </p>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.24)} className="mt-10 flex flex-wrap gap-3">
            <a
              href={LINKEDIN_JOBS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "default", size: "lg" }),
                "font-medium transition-opacity hover:opacity-90"
              )}
            >
              View open roles
              <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </a>
            <Link href="/about" className={cn(buttonVariants({ variant: "outline", size: "lg" }))}>
              About Proxidize
            </Link>
          </MotionDiv>
        </div>
      </section>

      <section
        className="full-bleed band-wash bg-band py-section border-border border-b"
        aria-labelledby="about-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
            <MotionDiv {...fadeUp(0)}>
              <p className="text-muted-foreground mb-4 text-sm font-medium">Who we are</p>
              <h2
                id="about-heading"
                className="text-foreground text-3xl leading-[1.15] font-bold tracking-tight sm:text-4xl"
              >
                Managed cloud proxies for the modern web.
              </h2>
            </MotionDiv>

            <MotionDiv {...fadeUp(0.1)} className="space-y-5">
              <p className="text-muted-foreground text-base leading-relaxed">
                Proxidize provides mobile and residential proxy solutions that let businesses and
                developers collect data, test applications, and automate workflows at scale, with
                reliable access and no bandwidth limits.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed">
                Our users range from independent researchers scraping public data to Fortune 500
                companies running enterprise-grade data pipelines. The common thread: they need
                proxies <span className="text-foreground font-medium">that work</span>, at volume,
                reliably.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed">
                Our mission is to make reliable data access a commodity, so the teams who need it
                can focus on what they build, not the infrastructure underneath it.
              </p>
            </MotionDiv>
          </div>

          <MotionDiv {...fadeUp(0.2)} className="mt-16 grid grid-cols-2 gap-8 sm:grid-cols-4">
            {[
              { value: STATS.countries, label: "Countries covered" },
              { value: "Millions of", label: "IP addresses" },
              { value: `${uptime.toFixed(3)}%`, label: "Uptime Guarantee" },
              { value: "50K+", label: "Customers served" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="text-foreground text-3xl font-bold tracking-tight">
                  {stat.value}
                </span>
                <span className="text-muted-foreground text-sm">{stat.label}</span>
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      <section className="py-section border-border border-b" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Why Proxidize</p>
            <h2
              id="why-heading"
              className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl"
            >
              What working here actually looks like.
            </h2>
          </MotionDiv>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whyItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <MotionDiv
                  key={item.title}
                  {...fadeUp(i * 0.08)}
                  className="group bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-7"
                >
                  <div
                    className="relative mb-5 flex h-10 w-10 items-center justify-center rounded-xl border transition-colors"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, transparent)",
                      borderColor: "color-mix(in srgb, var(--brand-green) 24%, transparent)",
                    }}
                  >
                    <Icon size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                  </div>
                  <h3 className="text-foreground relative mb-2 text-lg font-semibold">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground relative text-sm leading-relaxed">
                    {item.description}
                  </p>
                </MotionDiv>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="full-bleed band-wash bg-band py-section border-border border-b"
        aria-labelledby="process-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">How we hire</p>
            <h2
              id="process-heading"
              className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Straightforward. Respectful of your time.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed">
              From first conversation to offer, typically within 2 to 3 weeks.
            </p>
          </MotionDiv>

          <div className="relative">
            <div
              className="bg-border absolute top-10 right-0 left-0 hidden h-px lg:block"
              aria-hidden="true"
            />
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {hiringSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <MotionDiv
                    key={step.step}
                    {...fadeUp(i * 0.1)}
                    className="relative flex flex-col"
                  >
                    <div
                      className="relative z-10 mb-6 flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-2xl border"
                      style={{
                        backgroundColor:
                          "color-mix(in srgb, var(--brand-green) 10%, var(--background))",
                        borderColor: "color-mix(in srgb, var(--brand-green) 26%, transparent)",
                      }}
                    >
                      <Icon size={20} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                      <span className="text-muted-foreground text-[10px] font-bold tracking-widest">
                        {step.step}
                      </span>
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="text-foreground text-lg font-semibold">{step.title}</h3>
                      <span className="text-muted-foreground bg-muted/40 rounded-md px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </MotionDiv>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-section border-border border-b" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Perks & benefits</p>
            <h2
              id="benefits-heading"
              className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Built to support great work.
            </h2>
          </MotionDiv>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <MotionDiv
                  key={b.title}
                  {...fadeUp(i * 0.06)}
                  className="bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-6"
                >
                  <div
                    className="relative mb-4 flex h-9 w-9 items-center justify-center rounded-lg border"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, transparent)",
                      borderColor: "color-mix(in srgb, var(--brand-green) 24%, transparent)",
                    }}
                  >
                    <Icon size={16} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                  </div>
                  <h3 className="text-foreground relative mb-1.5 text-lg font-semibold">
                    {b.title}
                  </h3>
                  <p className="text-muted-foreground relative text-xs leading-relaxed">
                    {b.description}
                  </p>
                </MotionDiv>
              );
            })}
          </div>
        </div>
      </section>

      {/* The site-wide closing band. The buttons are this page's own — a job
          board, not a trial — and the trial reassurances don't apply here, so
          the perk row is dropped. Everything else is the shared CTA. */}
      <PricingCtaSection
        id="careers-cta-heading"
        heading="Interested?"
        body="We're always looking for people who care about infrastructure and want to work on problems that matter. Check our open roles and reach out."
        perks={[]}
        actions={
          <>
            <a
              href={LINKEDIN_JOBS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "default", size: "lg" }),
                "light:shadow-[0_10px_28px_-8px_rgb(var(--accent-blue-deep)/0.55)]"
              )}
            >
              View open roles
              <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </a>
            <Link href="/about" className={cn(buttonVariants({ variant: "outline", size: "lg" }))}>
              About Proxidize
            </Link>
          </>
        }
      />
    </main>
  );
}
